/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'classnames';
import { Tag } from '@ui/atoms/Tag/Tag';
import { translate } from '@utils/i18n';
import { Pagination } from './components/Pagination';
import { CustomScrollbar } from '../CustomScrollbar';
import { usePinnedTablePagination } from './hooks/usePinnedTablePagination';
import { ColumnVisibilityControl } from './components/ColumnVisibility/ColumnVisibilityControl';
import { useTableSelection } from './hooks/useTableSelection';
import { TableFilterRow } from './components/TableFilterRow';
import { TableMassToolbar } from './components/TableMassToolbar';
import { TableAccordionGroups } from './components/TableAccordionGroups';
import { TableGrid } from './components/TableGrid';
import { ANCHORED_TOOLBAR_LANE_PX } from '../Toolbar/useAnchoredSelectionToolbar';
import { buildActionOffsets, buildPinnedColumnLayout, SELECTOR_COL_WIDTH } from './tableUtils';
import './TableStyles.scss';
const TABLE_I18N_NS = 'ui.table';
function tableText(key, fallback, extras = {}) {
    const translated = translate(key, Object.assign({ namespace: TABLE_I18N_NS }, extras));
    if (translated === key || translated === `${TABLE_I18N_NS}.${key}`) {
        return fallback.replace(/%\{(\w+)\}/g, (match, name) => extras[name] === undefined ? match : String(extras[name]));
    }
    return translated;
}
export const TableSize = { Auto: 'auto', Full: 'full' };
const DEFAULT_GET_ITEM_ID = (item) => {
    const record = item;
    const { id } = record;
    if (id !== undefined && id !== null && (typeof id === 'string' || typeof id === 'number'))
        return id;
    if (process.env.NODE_ENV !== 'production') {
        console.warn('Table component: Item is missing a valid "id" property.', { item });
    }
    try {
        return JSON.stringify(item);
    }
    catch (_a) {
        return `fallback-${Object.keys(record).join('-')}`;
    }
};
export function Table({ data, columns, isGrouped = false, collapsibleGroups = false, collapseGroupLabel = 'Collapse group', expandGroupLabel = 'Expand group', variant = 'detail', size = TableSize.Auto, expandableRowRenderer, onRowClick, expandedRows = {}, hover = false, selectable = false, isRowSelectable, selectedItems = [], onSelectionChange, onSelectAll, tableId, sortable = false, sortableColumns, onSort, sortBy, sortDirection: controlledSortDirection, paginated = false, pageSize = 10, onPageChange, serverCurrentPage, serverTotalPages, serverTotalItems, pageSizeOptions = [], onPageSizeChange, paginationLabels, toolbar, selection, columnVisibility, groupedPresentation = 'rows', filterBar, showHeaderSelectAll = true, getItemId = DEFAULT_GET_ITEM_ID, className = '', activeItemId = null, onColumnMenuOpen, isLoading = false, loadingContent, loadingLabel = 'Loading', isError = false, errorContent, errorLabel = 'Unable to load data', emptyContent, enableCustomScrollbar = false, onFirstSelectedRowRef: onFirstSelectedRowRefProp }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const devWarnedRef = useRef(false);
    if (process.env.NODE_ENV !== 'production' &&
        !devWarnedRef.current &&
        selection !== undefined &&
        (selectedItems.length > 0 || onSelectionChange !== undefined || onSelectAll !== undefined)) {
        devWarnedRef.current = true;
        console.warn('Table: Both `selection` and controlled selection props (`selectedItems` / `onSelectionChange` / `onSelectAll`) were supplied. `selection` wins — the controlled props are ignored for internal selection.');
    }
    const tableIdRef = useRef();
    if (!tableIdRef.current)
        tableIdRef.current = `table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const uniqueTableId = tableId || tableIdRef.current;
    const isInternalSelection = selectable === true && selection !== undefined;
    // Filtered here rather than at each call site: this feeds `useTableSelection`, whose
    // select-all-entire path maps it straight to ids. Leaving the gate to `selectableData` alone
    // covered only the header toggle, so a table using internal selection could still pull a
    // rejected row in through the toolbar. Caught by CodeRabbit on PR #23862.
    const internalSelectionData = useMemo(() => {
        if (isLoading || isError)
            return [];
        const rawGroups = !isGrouped ? [{ data: data }] : data;
        const flat = rawGroups.flatMap((g) => g.data);
        return isRowSelectable ? flat.filter(isRowSelectable) : flat;
    }, [isLoading, isError, isGrouped, data, isRowSelectable]);
    const internalSelection = useTableSelection({
        data: internalSelectionData,
        getItemId,
        mode: (_a = selection === null || selection === void 0 ? void 0 : selection.mode) !== null && _a !== void 0 ? _a : 'page',
        serverTotalItems,
        resetKey: selection === null || selection === void 0 ? void 0 : selection.resetKey,
        onChange: selection === null || selection === void 0 ? void 0 : selection.onChange
    });
    const effectiveSelectedItems = isInternalSelection
        ? internalSelection.selectAllEntire
            ? internalSelectionData.map(getItemId)
            : internalSelection.selectedIds
        : selectedItems;
    const effectiveOnSelectionChange = isInternalSelection
        ? (id) => {
            internalSelection.toggleItem(id);
        }
        : onSelectionChange;
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDir, setSortDir] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [collapsedGroupIds, setCollapsedGroupIds] = useState(() => new Set());
    const toggleGroupCollapsed = useCallback((groupId) => {
        setCollapsedGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId))
                next.delete(groupId);
            else
                next.add(groupId);
            return next;
        });
    }, []);
    const handleSort = useCallback((dataKey, direction) => {
        setCurrentPage(1);
        if (onSort) {
            onSort(dataKey, direction);
            return;
        }
        setSortColumn(direction ? dataKey : null);
        setSortDir(direction);
    }, [onSort]);
    const dataGroups = useMemo(() => (!isGrouped ? [{ data: data }] : data), [isGrouped, data]);
    const allData = useMemo(() => dataGroups.flatMap((g) => g.data), [dataGroups]);
    const sortedGroups = useMemo(() => {
        if (onSort || !sortable || !sortColumn || !sortDir)
            return dataGroups;
        return dataGroups.map((group) => (Object.assign(Object.assign({}, group), { data: [...group.data].sort((a, b) => {
                const aVal = a[sortColumn];
                const bVal = b[sortColumn];
                if (aVal == null && bVal == null)
                    return 0;
                if (aVal == null)
                    return sortDir === 'asc' ? -1 : 1;
                if (bVal == null)
                    return sortDir === 'asc' ? 1 : -1;
                if (typeof aVal === 'number' && typeof bVal === 'number')
                    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
                const strA = String(aVal).toLowerCase();
                const strB = String(bVal).toLowerCase();
                return sortDir === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
            }) })));
    }, [dataGroups, sortable, sortColumn, sortDir, onSort]);
    const isServerPaginated = !!onPageChange;
    const clientTotalItems = sortedGroups.reduce((sum, g) => sum + g.data.length, 0);
    const totalItems = isServerPaginated ? serverTotalItems !== null && serverTotalItems !== void 0 ? serverTotalItems : clientTotalItems : clientTotalItems;
    let totalPages = 1;
    if (isServerPaginated)
        totalPages = serverTotalPages !== null && serverTotalPages !== void 0 ? serverTotalPages : 1;
    else if (paginated)
        totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const activePage = isServerPaginated ? serverCurrentPage !== null && serverCurrentPage !== void 0 ? serverCurrentPage : 1 : currentPage;
    useEffect(() => {
        if (!isServerPaginated && paginated && currentPage > totalPages)
            setCurrentPage(Math.max(1, totalPages));
    }, [isServerPaginated, paginated, currentPage, totalPages]);
    const paginatedGroups = useMemo(() => {
        if (isServerPaginated || !paginated)
            return sortedGroups;
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        let offset = 0;
        return sortedGroups
            .map((group) => {
            const filtered = group.data.filter((_row, i) => {
                const idx = offset + i;
                return idx >= start && idx < end;
            });
            offset += group.data.length;
            return Object.assign(Object.assign({}, group), { data: filtered });
        })
            .filter((g) => g.data.length > 0);
    }, [sortedGroups, isServerPaginated, paginated, currentPage, pageSize]);
    const selectionCandidates = isRowSelectable ? allData.filter(isRowSelectable) : allData;
    const selectableData = isLoading || isError ? [] : selectionCandidates;
    const selectedCount = selectableData.filter((item) => effectiveSelectedItems === null || effectiveSelectedItems === void 0 ? void 0 : effectiveSelectedItems.includes(getItemId(item))).length;
    const allSelected = selectable && selectableData.length > 0 && selectedCount === selectableData.length;
    const someSelected = selectable && selectedCount > 0 && !allSelected;
    const handleSelectAllToggle = useCallback(() => {
        if (isInternalSelection) {
            const ids = selectableData.map(getItemId);
            if (allSelected || someSelected)
                internalSelection.selectCurrentPage([]);
            else
                internalSelection.selectCurrentPage(ids);
            return;
        }
        if (!onSelectAll)
            return;
        if (allSelected || someSelected)
            onSelectAll(false);
        else
            onSelectAll(true);
    }, [isInternalSelection, allSelected, someSelected, selectableData, getItemId, internalSelection, onSelectAll]);
    const showToolbar = toolbar !== undefined || columnVisibility !== undefined || (selectable && selection !== undefined);
    const showSelection = selectable && showToolbar;
    let toolbarSelectionCount = 0;
    const toolbarSelectionTotal = serverTotalItems !== null && serverTotalItems !== void 0 ? serverTotalItems : allData.length;
    let toolbarSelectAll = false;
    let toolbarOnSelectAll = () => undefined;
    let toolbarSelectAllLabel = '';
    if (isInternalSelection) {
        toolbarSelectionCount = internalSelection.selectedCount;
        toolbarSelectAll = internalSelection.selectAllEntire;
        toolbarOnSelectAll = internalSelection.selectAllEntirely;
        toolbarSelectAllLabel = (_b = selection === null || selection === void 0 ? void 0 : selection.selectAllLabel) !== null && _b !== void 0 ? _b : '';
    }
    else if (selectable && toolbar) {
        toolbarSelectionCount = effectiveSelectedItems.length;
        toolbarOnSelectAll = () => {
            onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll(true);
        };
        toolbarSelectAllLabel = (_c = toolbar.selectAllLabel) !== null && _c !== void 0 ? _c : '';
    }
    const controlledSelectionState = useMemo(() => ({
        selectedIds: effectiveSelectedItems,
        selectedRows: allData.filter((item) => effectiveSelectedItems.includes(getItemId(item))),
        selectAllEntire: false,
        selectedCount: effectiveSelectedItems.length,
        clearSelection: () => {
            onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll(false);
        }
    }), [allData, effectiveSelectedItems, getItemId, onSelectAll]);
    const selectionStateForGroups = isInternalSelection
        ? internalSelection
        : controlledSelectionState;
    const groupingValue = (_e = (_d = columnVisibility === null || columnVisibility === void 0 ? void 0 : columnVisibility.grouping) === null || _d === void 0 ? void 0 : _d.value) !== null && _e !== void 0 ? _e : null;
    const groupingLabel = (_h = (_g = (_f = columnVisibility === null || columnVisibility === void 0 ? void 0 : columnVisibility.grouping) === null || _f === void 0 ? void 0 : _f.labels) === null || _g === void 0 ? void 0 : _g.groupedBy) !== null && _h !== void 0 ? _h : 'Grouped by';
    const configureCluster = columnVisibility ? (React.createElement(React.Fragment, null,
        groupingValue !== null && (React.createElement(Tag, { label: `${groupingLabel}: ${(_l = (_k = (_j = columnVisibility.grouping) === null || _j === void 0 ? void 0 : _j.options.find((o) => o.value === groupingValue)) === null || _k === void 0 ? void 0 : _k.label) !== null && _l !== void 0 ? _l : groupingValue}`, variant: "gray", colorStyle: "pastry", onRemove: () => {
                var _a;
                (_a = columnVisibility.grouping) === null || _a === void 0 ? void 0 : _a.onChange(null);
            } })),
        React.createElement(ColumnVisibilityControl, { columns: columnVisibility.items, visibleColumnKeys: columnVisibility.visibleKeys, defaultColumnKeys: columnVisibility.defaultKeys, onApply: columnVisibility.onApply, labels: columnVisibility.labels, grouping: columnVisibility.grouping }))) : null;
    const isAccordionGrouped = isGrouped && groupedPresentation === 'accordion';
    const currentPageIds = useMemo(() => paginatedGroups.flatMap((g) => (isRowSelectable ? g.data.filter(isRowSelectable) : g.data).map(getItemId)), [paginatedGroups, getItemId, isRowSelectable]);
    const accordionPageIds = isAccordionGrouped ? currentPageIds : [];
    const allPageSelected = isAccordionGrouped &&
        accordionPageIds.length > 0 &&
        accordionPageIds.every((id) => effectiveSelectedItems.includes(id));
    const somePageSelected = isAccordionGrouped && accordionPageIds.some((id) => effectiveSelectedItems.includes(id)) && !allPageSelected;
    const handleAccordionSelectAll = useCallback((checked) => {
        if (isInternalSelection) {
            if (checked)
                internalSelection.selectCurrentPage(accordionPageIds);
            else
                internalSelection.selectCurrentPage([]);
            return;
        }
        if (onSelectAll) {
            onSelectAll(checked);
            return;
        }
        // A caller that controls selection through `selectedItems` + `onSelectionChange` and does
        // not also hand in `onSelectAll` used to get a checkbox that did nothing at all. Fall back
        // to one change per row, the same way the group checkbox beneath it already does.
        accordionPageIds.forEach((id) => {
            effectiveOnSelectionChange === null || effectiveOnSelectionChange === void 0 ? void 0 : effectiveOnSelectionChange(id, checked);
        });
    }, [isInternalSelection, accordionPageIds, internalSelection, onSelectAll, effectiveOnSelectionChange]);
    const handleAccordionGroupSelect = useCallback((groupIds, checked) => {
        if (isInternalSelection) {
            groupIds.forEach((id) => {
                const sel = internalSelection.selectedIds.includes(id);
                if (checked && !sel)
                    internalSelection.toggleItem(id);
                else if (!checked && sel)
                    internalSelection.toggleItem(id);
            });
        }
        else
            groupIds.forEach((id) => {
                effectiveOnSelectionChange === null || effectiveOnSelectionChange === void 0 ? void 0 : effectiveOnSelectionChange(id, checked);
            });
    }, [isInternalSelection, internalSelection, effectiveOnSelectionChange]);
    const actionOffsets = useMemo(() => buildActionOffsets(columns), [columns]);
    const pinnedLayout = useMemo(() => buildPinnedColumnLayout(columns), [columns]);
    // First selected row anchor for the floating toolbar (task-12).
    // State (not ref) so that when the DOM element mounts the portal gets the real anchor.
    const [firstSelectedAnchor, setFirstSelectedAnchor] = useState(null);
    const [reserveToolbarLane, setReserveToolbarLane] = useState(false);
    const handleFirstSelectedRowRef = useCallback((el) => {
        onFirstSelectedRowRefProp === null || onFirstSelectedRowRefProp === void 0 ? void 0 : onFirstSelectedRowRefProp(el);
    }, [onFirstSelectedRowRefProp]);
    const showPagination = (paginated || isServerPaginated) && (isServerPaginated || totalPages > 1);
    const { tableRef, paginationRef, isPinned, pinHeight } = usePinnedTablePagination(enableCustomScrollbar, showPagination);
    // Anchor to the first checked checkbox's row. The header select-all checkbox
    // comes first in the DOM, so selecting the page puts the bar above the table.
    useLayoutEffect(() => {
        var _a, _b, _c, _d;
        if (toolbarSelectionCount === 0) {
            setFirstSelectedAnchor(null);
            setReserveToolbarLane(false);
            return;
        }
        const root = tableRef.current;
        if (!root)
            return;
        const checked = root.querySelector('input[type="checkbox"]:checked');
        // With no checkbox checked on this page (controlled IDs from other pages), fall back to the
        // first row under the lane — the root also wraps the filter row, so anchoring to it would
        // float the bar above the filters.
        const anchor = (_c = (_b = (_a = checked === null || checked === void 0 ? void 0 : checked.closest('tr')) !== null && _a !== void 0 ? _a : checked === null || checked === void 0 ? void 0 : checked.closest('.qmb-ui-table__accordion-select-all')) !== null && _b !== void 0 ? _b : root.querySelector('thead tr, .qmb-ui-table__accordion-select-all')) !== null && _c !== void 0 ? _c : root;
        setFirstSelectedAnchor(anchor);
        setReserveToolbarLane(!((_d = checked === null || checked === void 0 ? void 0 : checked.closest('tr')) === null || _d === void 0 ? void 0 : _d.closest('tbody')));
    }, [toolbarSelectionCount, effectiveSelectedItems, tableRef]);
    let synthesizedToolbar;
    if (selectable && selection !== undefined) {
        synthesizedToolbar = { groups: [] };
    }
    else if (columnVisibility && !filterBar) {
        synthesizedToolbar = { groups: [] };
    }
    const effectiveToolbar = toolbar !== null && toolbar !== void 0 ? toolbar : synthesizedToolbar;
    // Locale-keyed menu labels for the new Toolbar.Selection API.
    // Callers that supply `selection` own the select-matching action; controlled callers don't.
    const selectionMenuProps = useMemo(() => ({
        selectedLabel: showSelection && toolbarSelectionCount > 0
            ? tableText('n_selected', `${toolbarSelectionCount} selected`, { count: toolbarSelectionCount })
            : undefined,
        selectPageLabel: showSelection ? tableText('select_all_on_this_page', 'Select all on this page') : undefined,
        onSelectPage: showSelection
            ? () => {
                // Menu item is "Select all on this page" — always select, never toggle/clear.
                // handleSelectAllToggle clears whenever someSelected, which is the only
                // reachable state for this menu (IP-16193).
                if (isInternalSelection) {
                    internalSelection.selectCurrentPage(currentPageIds, { replace: true });
                }
                else {
                    onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll(true);
                }
            }
            : undefined,
        selectMatchingLabel: showSelection && isInternalSelection && (selection === null || selection === void 0 ? void 0 : selection.mode) === 'entire' && toolbarSelectAllLabel
            ? toolbarSelectAllLabel
            : undefined,
        onSelectMatching: showSelection && isInternalSelection && (selection === null || selection === void 0 ? void 0 : selection.mode) === 'entire' ? toolbarOnSelectAll : undefined,
        clearLabel: showSelection ? tableText('clear_selection', 'Clear selection') : undefined,
        onClear: showSelection
            ? () => {
                if (isInternalSelection)
                    internalSelection.clearSelection();
                else
                    onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll(false);
            }
            : undefined
    }), [
        showSelection,
        toolbarSelectionCount,
        currentPageIds,
        isInternalSelection,
        selection === null || selection === void 0 ? void 0 : selection.mode,
        toolbarSelectAllLabel,
        toolbarOnSelectAll,
        internalSelection,
        onSelectAll
    ]);
    return (React.createElement("div", { ref: tableRef, className: clsx('qmb-ui-table', className, {
            'qmb-ui-table--listing': variant === 'listing',
            'qmb-ui-table--detail': variant === 'detail',
            'qmb-ui-table--grouped': isGrouped,
            'qmb-ui-table--x-full': size === TableSize.Full,
            'qmb-ui-table--custom-scrollbar': enableCustomScrollbar
        }), style: selectable ? { '--selector-col-width': SELECTOR_COL_WIDTH } : undefined },
        filterBar && (React.createElement(TableFilterRow, { filterBar: filterBar, configureCluster: columnVisibility ? configureCluster : null })),
        showToolbar && effectiveToolbar && (React.createElement(TableMassToolbar, { toolbar: effectiveToolbar, selectionCount: toolbarSelectionCount, selectionTotal: toolbarSelectionTotal, selectAll: toolbarSelectAll, onSelectAll: toolbarOnSelectAll, selectAllLabel: toolbarSelectAllLabel, showSelection: showSelection, selectionState: selectionStateForGroups, configureCluster: configureCluster, hasFilterBar: !!filterBar, anchorElement: firstSelectedAnchor !== null && firstSelectedAnchor !== void 0 ? firstSelectedAnchor : undefined, contentWidth: "100%", selectedLabel: selectionMenuProps.selectedLabel, selectPageLabel: selectionMenuProps.selectPageLabel, onSelectPage: selectionMenuProps.onSelectPage, selectMatchingLabel: selectionMenuProps.selectMatchingLabel, onSelectMatching: selectionMenuProps.onSelectMatching, clearLabel: selectionMenuProps.clearLabel, onClear: selectionMenuProps.onClear })),
        showToolbar && effectiveToolbar && reserveToolbarLane && (React.createElement("div", { className: "qmb-ui-table__floating-toolbar-lane", "aria-hidden": "true", style: { height: ANCHORED_TOOLBAR_LANE_PX } })),
        isAccordionGrouped ? (React.createElement(TableAccordionGroups, { tableId: uniqueTableId, paginatedGroups: paginatedGroups, columns: columns, size: size, hover: hover, selectable: selectable, isRowSelectable: isRowSelectable, selectedItems: effectiveSelectedItems, onSelectionChange: effectiveOnSelectionChange, getItemId: getItemId, sortable: sortable, sortableColumns: sortableColumns, onSort: onSort, sortBy: sortBy, sortDirection: controlledSortDirection, onRowClick: onRowClick, expandableRowRenderer: expandableRowRenderer, expandedRows: expandedRows, activeItemId: activeItemId, allPageSelected: allPageSelected, somePageSelected: somePageSelected, handleAccordionSelectAll: handleAccordionSelectAll, handleAccordionGroupSelect: handleAccordionGroupSelect, effectiveSelectedItems: effectiveSelectedItems, emptyContent: emptyContent, onFirstSelectedRowRef: handleFirstSelectedRowRef })) : enableCustomScrollbar ? (React.createElement(CustomScrollbar, null,
            React.createElement("div", { className: "qmb-ui-table__scroll --scroll-x" },
                React.createElement(TableGrid, { tableId: uniqueTableId, size: size, columns: columns, paginatedGroups: paginatedGroups, allData: allData, selectable: selectable, isRowSelectable: isRowSelectable, showHeaderSelectAll: showHeaderSelectAll, selectedItems: effectiveSelectedItems, onSelectionChange: effectiveOnSelectionChange, handleSelectAllToggle: handleSelectAllToggle, allSelected: allSelected, someSelected: someSelected, sortable: sortable, sortableColumns: sortableColumns, onSort: onSort, sortBy: sortBy, sortDirection: controlledSortDirection, sortColumn: sortColumn, sortDir: sortDir, handleSort: handleSort, onColumnMenuOpen: onColumnMenuOpen, onRowClick: onRowClick, expandedRows: expandedRows, expandableRowRenderer: expandableRowRenderer, hover: hover, activeItemId: activeItemId, isLoading: isLoading, isError: isError, loadingContent: loadingContent, loadingLabel: loadingLabel, errorContent: errorContent, errorLabel: errorLabel, emptyContent: emptyContent, collapsibleGroups: collapsibleGroups, collapseGroupLabel: collapseGroupLabel, expandGroupLabel: expandGroupLabel, isGrouped: isGrouped, getItemId: getItemId, collapsedGroupIds: collapsedGroupIds, toggleGroupCollapsed: toggleGroupCollapsed, actionOffsets: actionOffsets, pinnedLayout: pinnedLayout, onFirstSelectedRowRef: handleFirstSelectedRowRef })))) : (React.createElement(TableGrid, { tableId: uniqueTableId, size: size, columns: columns, paginatedGroups: paginatedGroups, allData: allData, selectable: selectable, isRowSelectable: isRowSelectable, showHeaderSelectAll: showHeaderSelectAll, selectedItems: effectiveSelectedItems, onSelectionChange: effectiveOnSelectionChange, handleSelectAllToggle: handleSelectAllToggle, allSelected: allSelected, someSelected: someSelected, sortable: sortable, sortableColumns: sortableColumns, onSort: onSort, sortBy: sortBy, sortDirection: controlledSortDirection, sortColumn: sortColumn, sortDir: sortDir, handleSort: handleSort, onColumnMenuOpen: onColumnMenuOpen, onRowClick: onRowClick, expandedRows: expandedRows, expandableRowRenderer: expandableRowRenderer, hover: hover, activeItemId: activeItemId, isLoading: isLoading, isError: isError, loadingContent: loadingContent, loadingLabel: loadingLabel, errorContent: errorContent, errorLabel: errorLabel, emptyContent: emptyContent, collapsibleGroups: collapsibleGroups, collapseGroupLabel: collapseGroupLabel, expandGroupLabel: expandGroupLabel, isGrouped: isGrouped, getItemId: getItemId, collapsedGroupIds: collapsedGroupIds, toggleGroupCollapsed: toggleGroupCollapsed, actionOffsets: actionOffsets, pinnedLayout: pinnedLayout, onFirstSelectedRowRef: handleFirstSelectedRowRef })),
        showPagination && (React.createElement(React.Fragment, null,
            React.createElement("div", { ref: paginationRef, className: clsx('qmb-ui-table__pagination-anchor', {
                    'qmb-ui-table__pagination-anchor--pinned': isPinned
                }) },
                React.createElement(Pagination, { currentPage: activePage, totalPages: totalPages, totalItems: totalItems, pageSize: pageSize, onPageChange: onPageChange || setCurrentPage, pageSizeOptions: pageSizeOptions, onPageSizeChange: onPageSizeChange, labels: paginationLabels })),
            enableCustomScrollbar && (React.createElement("div", { className: "qmb-ui-table__pagination-pin-spacer", "data-testid": "table-pagination-pin-spacer", "aria-hidden": true, style: { height: pinHeight } }))))));
}
Table.displayName = 'Table';
